/**
 * Domain Endpoint - matches PHP SDK Domain endpoint
 */

import { DreamscapeClient } from '../client';
import {
  DomainAvailabilityResult,
  DomainTld,
  DomainExisting,
  DomainRegister,
  DomainRenew,
  DomainUpdate,
  DomainCheckAvailabilityFilter,
  DomainGetAllFilter,
  Collection,
} from '../types';
import { DomainRegistrantEndpoint } from './domain-registrant';

// Fix: Export DomainRegistrantEndpoint for use
export { DomainRegistrantEndpoint };

export class DomainEndpoint {
  public readonly registrants: DomainRegistrantEndpoint;

  constructor(private client: DreamscapeClient) {
    this.registrants = new DomainRegistrantEndpoint(client);
  }

  /**
   * Get available TLDs with prices
   */
  async getAvailableTlds(filters?: {
    customerId?: number;
    currency?: string;
    tlds?: string[];
    page?: number;
    limit?: number;
  }): Promise<Collection<DomainTld>> {
    const params: Record<string, any> = {};
    if (filters?.customerId) params.customer_id = filters.customerId;
    if (filters?.currency) params.currency = filters.currency;
    if (filters?.tlds) params.tlds = filters.tlds;
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    return this.client.request<Collection<DomainTld>>('GET', 'domains/tlds', undefined, params);
  }

  /**
   * Check domain availability
   */
  async checkAvailability(
    domainNames: string[],
    filters?: DomainCheckAvailabilityFilter
  ): Promise<Collection<DomainAvailabilityResult>> {
    if (!domainNames || domainNames.length === 0) {
      throw new Error('Domain names array must not be empty');
    }

    const params: Record<string, any> = {
      domain_names: domainNames,
    };
    
    if (filters?.customerId) params.customer_id = filters.customerId;
    if (filters?.currency) params.currency = filters.currency;

    return this.client.request<Collection<DomainAvailabilityResult>>(
      'GET',
      'domains/availability',
      undefined,
      params
    );
  }

  /**
   * Get all domains
   */
  async getAll(filters?: DomainGetAllFilter): Promise<Collection<DomainExisting>> {
    const params: Record<string, any> = {};
    if (filters?.customerId) params.customer_id = filters.customerId;
    if (filters?.statusId) params.status_id = filters.statusId;
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    return this.client.request<Collection<DomainExisting>>('GET', 'domains', undefined, params);
  }

  /**
   * Get domain details
   */
  async getDetails(domainId: number): Promise<DomainExisting> {
    if (domainId < 1) {
      throw new Error('Domain ID must be a positive integer');
    }

    return this.client.request<DomainExisting>('GET', `domains/${domainId}`);
  }

  /**
   * Register a domain
   */
  async register(data: DomainRegister): Promise<DomainExisting> {
    // Convert to API format
    const requestData: any = {
      domain_name: data.domainName,
      customer_id: data.customerId,
      period: data.period,
    };

    if (data.registrantId) requestData.registrant_id = data.registrantId;
    if (data.adminContactId) requestData.admin_contact_id = data.adminContactId;
    if (data.billingContactId) requestData.billing_contact_id = data.billingContactId;
    if (data.techContactId) requestData.tech_contact_id = data.techContactId;
    if (data.nameServers) requestData.name_servers = data.nameServers;
    if (data.privacy !== undefined) requestData.privacy = data.privacy;
    if (data.eligibility) requestData.eligibility_data = data.eligibility;
    if (data.confirmPremiumOrder !== undefined) requestData.confirm_premium_order = data.confirmPremiumOrder;

    return this.client.request<DomainExisting>('POST', 'domains', requestData);
  }

  /**
   * Update domain
   */
  async update(domainId: number, data: DomainUpdate): Promise<DomainExisting> {
    if (domainId < 1) {
      throw new Error('Domain ID must be a positive integer');
    }

    const requestData: any = {};
    if (data.nameServers) requestData.name_servers = data.nameServers;
    if (data.isLocked !== undefined) requestData.is_locked = data.isLocked;

    return this.client.request<DomainExisting>('PATCH', `domains/${domainId}`, requestData);
  }

  /**
   * Renew domain
   */
  async renew(domainId: number, data?: DomainRenew): Promise<DomainExisting> {
    if (domainId < 1) {
      throw new Error('Domain ID must be a positive integer');
    }

    const requestData: any = {};
    if (data?.period) requestData.period = data.period;

    return this.client.request<DomainExisting>('POST', `domains/${domainId}/renewal`, requestData);
  }

  /**
   * Terminate domain
   */
  async terminate(domainId: number): Promise<boolean> {
    if (domainId < 1) {
      throw new Error('Domain ID must be a positive integer');
    }

    await this.client.request('DELETE', `domains/${domainId}`);
    return true;
  }
}

